import React from 'react';
import {
  Users,
  Image,
  Shield,
  MapPin,
  Database,
  Globe,
  Search,
  BookOpen,
  Hammer,
  Scale,
  Footprints,
  ArrowUpRight,
  MessageCircle,
  LucideIcon,
} from 'lucide-react';

interface ContentsProps {
  onNavigate?: (page: string) => void;
}

interface Entry {
  code: string;
  title: string;
  blurb: string;
  icon: LucideIcon;
  page?: string;
}

const disciplines: Entry[] = [
  { code: 'HUMINT', title: 'Human Intelligence', blurb: 'Interviews, elicitation, and human-sourced information.', icon: MessageCircle, page: 'humint' },
  { code: 'SOCMINT', title: 'Social Media Intelligence', blurb: 'Profiles, posts, and networks across social platforms.', icon: Users, page: 'socmint' },
  { code: 'IMINT', title: 'Image Intelligence', blurb: 'Reverse search, metadata, and visual verification.', icon: Image, page: 'imint' },
  { code: 'CYBINT', title: 'Cyber Intelligence', blurb: 'Domains, breaches, infrastructure, and footprints.', icon: Shield, page: 'cybint' },
  { code: 'GEOINT', title: 'Geospatial Intelligence', blurb: 'Location, satellite imagery, and chronolocation.', icon: MapPin, page: 'geoint' },
  { code: 'FININT', title: 'Financial Intelligence', blurb: 'Transactions, entities, and money-trail analysis.', icon: Database, page: 'finint' },
  { code: 'DARKINT', title: 'Dark Web Intelligence', blurb: 'Onion services, marketplaces, and hidden forums.', icon: Globe, page: 'darkint' },
  { code: 'WEAPINT', title: 'Weapons Intelligence', blurb: 'Arms identification, markings, and provenance.', icon: Search, page: 'weapint' },
];

const resources: Entry[] = [
  { code: 'START', title: 'Introduction to OSINT', blurb: 'What OSINT is and how to think like an investigator.', icon: BookOpen },
  { code: 'TOOLS', title: 'Tools & Techniques', blurb: 'The core toolkit — dorking, scrapers, and aggregators.', icon: Hammer, page: 'tools' },
  { code: 'ETHICS', title: 'Legal & Ethical Considerations', blurb: 'Staying lawful, safe, and responsible.', icon: Scale },
  { code: 'CASES', title: 'OSINT Walkthrough', blurb: 'A full investigation, start to finish.', icon: Footprints, page: 'walkthrough' },
];

const Contents: React.FC<ContentsProps> = ({ onNavigate }) => {
  const renderCard = (entry: Entry, index: number) => {
    const Icon = entry.icon;
    const clickable = Boolean(entry.page && onNavigate);
    return (
      <button
        key={entry.code}
        onClick={() => clickable && onNavigate!(entry.page!)}
        disabled={!clickable}
        className={`group relative text-left bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6 overflow-hidden transition-all duration-300 ${
          clickable
            ? 'hover:-translate-y-1 hover:shadow-xl hover:border-orange-300 cursor-pointer'
            : 'opacity-70 cursor-default'
        }`}
      >
        {/* accent bar */}
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-orange-500 to-amber-600 scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-300" />

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold tracking-widest text-orange-500 uppercase">{entry.code}</span>
              {clickable && (
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-orange-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              )}
            </div>
            <h3 className="font-bold text-slate-900 dark:text-slate-100 leading-snug">{entry.title}</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 leading-relaxed">{entry.blurb}</p>
          </div>
        </div>

        <span className="absolute bottom-3 right-4 text-5xl font-black text-gray-50 group-hover:text-orange-50 transition-colors select-none">
          {String(index + 1).padStart(2, '0')}
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 p-8">
      <div className="max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <p className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-1">Field Guide</p>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">Contents</h1>
          </div>
        </div>
        <p className="text-gray-500 dark:text-slate-400 max-w-2xl mb-8">
          Every discipline of open-source intelligence, mapped out. Pick a track to jump straight in.
        </p>

        <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-12" />

        {/* Disciplines */}
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-5">Intelligence Disciplines</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
          {disciplines.map((d, i) => renderCard(d, i))}
        </div>

        {/* Resources */}
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-5">Foundations & Practice</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {resources.map((r, i) => renderCard(r, i + disciplines.length))}
        </div>
      </div>
    </div>
  );
};

export default Contents;
